import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { Search, ChevronRight, FileText, Layers, Book } from 'lucide-react';
import { getAllExams } from '../utils/examAPI';
import { getExamSubjectsCount } from '../utils/subjectHistorysAPI';

/**
 * 批量获取考试统计信息
 * @param {Array} exams - 考试列表
 * @returns {Object} 考试统计数据
 */
const getExamsStatsBatch = async (exams) => {
  try {
    const stats = {};
    
    // 为每个考试创建初始统计对象
    for (const exam of exams) {
      stats[exam._id] = {
        subjectCount: 0,
        questionCount: exam.count || 0
      };
      
      // 获取科目数量
      const subjectCount = await getExamSubjectsCount(exam._id);
      stats[exam._id].subjectCount = subjectCount;
    }
    
    return stats;
  } catch (error) {
    console.error('获取考试统计信息错误:', error);
    return {};
  }
};

/**
 * 题库练习页面组件
 * 显示各种考试类型及其包含的科目数量和题目总数
 */
function QuestionBank() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examStats, setExamStats] = useState({});

  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true);
        
        // 获取所有考试类型
        const data = await getAllExams();
        setExams(data || []);
        
        // 获取每种考试类型的统计信息
        const stats = await getExamsStatsBatch(data);
        setExamStats(stats);
        
      } catch (error) {
        console.error('Error fetching exams:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchExams();
  }, []);

  return (
    <div className="pb-24">
      <Header title="题库练习" showBack={false} />
      
      <div className="p-4">
        <div className="relative mb-6">
          <input
            type="text"
            placeholder="搜索考试..."
            className="w-full bg-white py-3 px-4 pr-10 rounded-lg shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <div className="absolute right-3 top-3 text-gray-400">
            <Search size={20} />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <Book className="w-5 h-5 text-primary-dark mr-2" />
          选择考试类型
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {exams.length > 0 ? (
              exams.map((exam) => (
                <Link
                  key={exam._id}
                  to={`/exam-subjects/${exam._id}`}
                  className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-purple-100 text-primary flex items-center justify-center mr-4">
                      <Book className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{exam.name}</h3>
                      <div className="flex text-sm text-gray-500 mt-1 space-x-3">
                        {examStats[exam._id] && (
                          <div className="flex items-center">
                            <Layers className="w-3 h-3 mr-1" />
                            <span>{examStats[exam._id].subjectCount}个科目</span>
                          </div>
                        )}
                        {examStats[exam._id] && (
                          <div className="flex items-center">
                            <FileText className="w-3 h-3 mr-1" />
                            <span>{examStats[exam._id].questionCount}题</span>
                          </div>
                        )}
                       
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Book className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p>暂无题库数据</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <TabBar />
    </div>
  );
}

export default QuestionBank; 